/**
 * Mounts Zendesk Messenger for authenticated, non-Pending users when the
 * `support-zendesk` release flag is ON. Pass `suppressed` on Tickets, Roadmap,
 * and Sprints — the corner there is the roadmap agent, and an open chat closes.
 *
 * Zendesk's native launcher bubble is suppressed (it covers pagination). We
 * render a custom chat FAB instead — click to toggle open/close, drag to
 * nudge within a bottom-right pocket (~one FAB-width, capped). The FAB homes
 * to the pocket center until the user drags; a drag is the only pin. Full-page
 * drag parked the bubble on form fields and looked like a bug. The
 * messenger panel is repositioned near the FAB via Zendesk
 * `customization.position` on open **and while dragging when open**.
 *
 * Re-authenticates when the TruSpace user id changes so chat history does
 * not leak across accounts. Logs out of Messenger when the session ends.
 */

import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { zendeskChatToken } from '@/api/endpoints/support-zendesk';
import { cn } from '@/lib/cn';
import {
  closeZendeskMessenger,
  getZendeskLauncherPosition,
  initZendesk,
  isZendeskKeyConfigured,
  isZendeskStageReplica,
  loginZendeskUser,
  logoutZendeskUser,
  openZendeskMessengerNearFab,
  setZendeskLauncherPosition,
  setZendeskMessengerPositionNearFab,
  subscribeZendeskMessengerOpenState,
  wireNativeLauncherSuppression,
  type ZendeskLauncherPos,
} from '@/lib/zendesk';
import {
  ZENDESK_FAB_SIZE_PX,
  clampZendeskLauncherPos,
  classifyZendeskFabPointerEnd,
  defaultZendeskLauncherPos,
} from '@/lib/zendesk-launcher-pos';
import { isReleaseFeatureEnabled } from '@/lib/release-flags';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Injects Messenger, suppresses the vendor launcher, and renders a custom
 * chat button (click toggles the panel; drag nudges within the corner).
 */
export function ZendeskMessenger({ suppressed = false }: { suppressed?: boolean }) {
  const user = useAuthStore((s) => s.user);
  const [pos, setPos] = useState<ZendeskLauncherPos>(() => {
    const saved = getZendeskLauncherPosition();
    return saved ? clampZendeskLauncherPos(saved) : defaultZendeskLauncherPos();
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const isOpenRef = useRef(isOpen);
  const posRef = useRef(pos);
  const dragRafRef = useRef<number | null>(null);

  // Keep drag/open handlers reading latest values without rebinding listeners.
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);
  useEffect(() => {
    posRef.current = pos;
  }, [pos]);

  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originLeft: number;
    originTop: number;
    moved: boolean;
  } | null>(null);

  const zendeskEnabled = isReleaseFeatureEnabled('support-zendesk');
  const isAuthed = Boolean(user);
  const isPending = user?.role === 'Pending';
  const stageReplica = isZendeskStageReplica();
  // Defer Messenger until claim / force-password gates clear — avoids a burst of
  // chat-token 403 toasts while the shell is parked on /profile. Disabled in Stage
  // replica mode so the storm matches RC11/Stage (Messenger mounts immediately).
  const claimGateActive =
    !stageReplica &&
    (user?.mustChangePassword === true ||
      user?.mustCompleteName === true ||
      user?.mustAcceptLegal === true);
  const userId = user?.userId;
  const shouldMount =
    zendeskEnabled &&
    isAuthed &&
    !isPending &&
    !claimGateActive &&
    typeof userId === 'number' &&
    isZendeskKeyConfigured();

  useEffect(() => {
    if (!shouldMount || typeof userId !== 'number') {
      // Session ended / Pending / claim gate — clear Messenger identity.
      logoutZendeskUser();
      return;
    }

    initZendesk();
    const unwire = wireNativeLauncherSuppression();
    const unsubOpenState = subscribeZendeskMessengerOpenState(setIsOpen);
    loginZendeskUser(async () => {
      const { jwt } = await zendeskChatToken();
      return jwt;
    }, userId);

    return () => {
      unsubOpenState();
      unwire();
      // Stage replica: RC11 called logout on cleanup — pairs with StrictMode /
      // parent re-renders to re-register loginUser and storm chat-token.
      if (stageReplica) {
        logoutZendeskUser();
      }
    };
  }, [shouldMount, userId, stageReplica]);

  useEffect(() => {
    if (suppressed) closeZendeskMessenger();
  }, [suppressed]);

  // Persist a snap-back when a stale mid-page position (old free-roam drag)
  // is clamped into the bottom-right pocket on first paint.
  useEffect(() => {
    const saved = getZendeskLauncherPosition();
    if (!saved) return;
    const next = clampZendeskLauncherPos(saved);
    if (next.left !== saved.left || next.top !== saved.top) {
      setZendeskLauncherPosition(next);
    }
  }, []);

  // Unpinned FABs follow the pocket center as the viewport changes. A saved
  // drag stays put and is only clamped so it cannot leave the pocket.
  useEffect(() => {
    function onResize() {
      const saved = getZendeskLauncherPosition();
      if (!saved) {
        setPos(defaultZendeskLauncherPos());
        return;
      }
      setPos((current) => {
        const next = clampZendeskLauncherPos(current);
        if (next.left !== saved.left || next.top !== saved.top) {
          setZendeskLauncherPosition(next);
        }
        return next;
      });
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  function handlePointerDown(event: ReactPointerEvent<HTMLButtonElement>) {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originLeft: pos.left,
      originTop: pos.top,
      moved: false,
    };
  }

  function syncMessengerToFab(next: ZendeskLauncherPos) {
    if (!isOpenRef.current) return;
    if (dragRafRef.current != null) {
      window.cancelAnimationFrame(dragRafRef.current);
    }
    dragRafRef.current = window.requestAnimationFrame(() => {
      dragRafRef.current = null;
      setZendeskMessengerPositionNearFab(next, ZENDESK_FAB_SIZE_PX);
    });
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLButtonElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (classifyZendeskFabPointerEnd('pointerup', dx, dy) !== 'drag') return;
    drag.moved = true;
    setIsDragging(true);
    const next = clampZendeskLauncherPos({
      left: drag.originLeft + dx,
      top: drag.originTop + dy,
    });
    setPos(next);
    syncMessengerToFab(next);
  }

  function endPointerCapture(event: ReactPointerEvent<HTMLButtonElement>): void {
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // Already released.
    }
    if (dragRafRef.current != null) {
      window.cancelAnimationFrame(dragRafRef.current);
      dragRafRef.current = null;
    }
  }

  function handlePointerCancel(event: ReactPointerEvent<HTMLButtonElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    endPointerCapture(event);
    dragRef.current = null;
    setIsDragging(false);
    setPos({ left: drag.originLeft, top: drag.originTop });
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLButtonElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    endPointerCapture(event);
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    const gesture = classifyZendeskFabPointerEnd('pointerup', dx, dy);
    dragRef.current = null;
    setIsDragging(false);
    if (gesture === 'drag' || drag.moved) {
      const next = clampZendeskLauncherPos({
        left: drag.originLeft + dx,
        top: drag.originTop + dy,
      });
      setPos(next);
      setZendeskLauncherPosition(next);
      syncMessengerToFab(next);
      return;
    }
    if (isOpen) {
      closeZendeskMessenger();
      return;
    }
    openZendeskMessengerNearFab(posRef.current, ZENDESK_FAB_SIZE_PX);
  }

  if (!shouldMount || suppressed) return null;

  return (
    <button
      type="button"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      style={{ left: pos.left, top: pos.top, width: ZENDESK_FAB_SIZE_PX, height: ZENDESK_FAB_SIZE_PX }}
      className={cn(
        'fixed z-sidebar flex touch-none items-center justify-center rounded-full bg-tertiary text-tertiary-foreground shadow-lg transition-shadow hover:bg-tertiary/90 hover:shadow-xl active:scale-95',
        isDragging ? 'cursor-grabbing' : 'cursor-pointer',
      )}
      title={
        isOpen
          ? 'Support chat — click to close, drag to nudge'
          : 'Support chat — click to open, drag to nudge'
      }
      aria-label={
        isOpen
          ? 'Close support chat. Drag to nudge in the corner.'
          : 'Open support chat. Drag to nudge in the corner.'
      }
      aria-pressed={isOpen}
    >
      {isOpen ? (
        <X className="h-6 w-6" aria-hidden />
      ) : (
        <MessageCircle className="h-6 w-6" aria-hidden />
      )}
    </button>
  );
}
